import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { API_ORIGIN } from '../lib/api.js';

function Qr({ value, size = 120 }) {
  const [svg, setSvg] = useState(null);
  useEffect(() => {
    let alive = true;
    QRCode.toString(value, { type: 'svg', margin: 1, errorCorrectionLevel: 'M' })
      .then((s) => alive && setSvg(s))
      .catch(() => alive && setSvg(null));
    return () => {
      alive = false;
    };
  }, [value]);
  if (!svg) return null;
  return (
    <div
      style={{ width: size, height: size }}
      className="mx-auto [&>svg]:h-full [&>svg]:w-full"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

const Row = ({ left, right, bold }) => (
  <div className={`flex justify-between gap-2 ${bold ? 'font-bold' : ''}`}>
    <span className="min-w-0">{left}</span>
    <span className="shrink-0 tabular-nums">{right}</span>
  </div>
);

const Rule = () => <div className="my-1.5 border-t border-dashed border-black" />;

/**
 * Receipt laid out like a thermal printout. The same component is used for the on-screen
 * preview, the browser-print fallback and the public e-receipt page.
 * `model` comes from buildReceiptModel() (shared) via the API.
 */
export function ReceiptView({ model, className = '' }) {
  if (!model) return null;
  const narrow = model.paperWidth === '58mm';
  return (
    <div
      className={`receipt relative mx-auto bg-white px-2 py-3 font-mono text-black ${narrow ? 'w-[58mm] text-[10px]' : 'w-[80mm] text-[11.5px]'} leading-snug ${className}`}
    >
      {model.watermark && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-5xl font-black tracking-widest text-black/10 -rotate-12">
          {model.watermark}
        </div>
      )}
      <div className="text-center">
        {model.business.logoUrl && (
          <img src={`${API_ORIGIN}${model.business.logoUrl}`} alt="" className="mx-auto mb-1 max-h-14 grayscale" />
        )}
        <p className="text-[1.25em] font-bold">{model.business.name}</p>
        {model.business.legalName && model.business.legalName !== model.business.name && (
          <p>{model.business.legalName}</p>
        )}
        <p>{model.outlet.name}</p>
        {model.outlet.addressLines.map((l) => (
          <p key={l}>{l}</p>
        ))}
        {model.outlet.phone && <p>Ph: {model.outlet.phone}</p>}
        {model.gstin && <p>GSTIN: {model.gstin}</p>}
        {model.fssai && <p>FSSAI: {model.fssai}</p>}
        {model.headerLines.map((l, i) => (
          <p key={i}>{l}</p>
        ))}
        <p className="mt-1 font-bold">{model.title}</p>
      </div>
      <Rule />
      {model.meta.map((m) => (
        <Row key={m.label} left={m.label} right={m.value} />
      ))}
      <Rule />
      <div className="flex justify-between font-bold">
        <span>Item</span>
        <span>Qty × Rate = Amt</span>
      </div>
      <Rule />
      {model.lines.map((l, i) => (
        <div key={i} className="mb-1">
          <p className="font-semibold">{l.name}</p>
          {l.modifiers.length > 0 && <p className="pl-2">+ {l.modifiers.join(', ')}</p>}
          {l.note && <p className="pl-2 italic">“{l.note}”</p>}
          <Row left={`  ${l.qty} × ${l.rate}`} right={l.amount} />
        </div>
      ))}
      <Rule />
      {model.totals.map((t) => (
        <Row key={t.label} left={t.label} right={t.value} />
      ))}
      <Rule />
      <Row left={model.grandTotalLabel ?? 'TOTAL'} right={`₹${model.grandTotal}`} bold />
      {model.taxNote && <p className="text-center text-[0.9em]">({model.taxNote})</p>}
      <Rule />
      {model.payments.map((p, i) => (
        <div key={i}>
          <Row left={`Paid: ${p.label}`} right={p.amount} />
          {p.tendered && <Row left="  Received" right={p.tendered} />}
          {p.change && <Row left="  Change" right={p.change} />}
        </div>
      ))}
      {model.due && <Row left="Due (credit)" right={model.due} bold />}
      {model.taxBreakup.length > 0 && (
        <>
          <Rule />
          <p className="text-center font-semibold">Tax summary</p>
          {model.taxBreakup.map((r) => (
            <div key={r.rate}>
              <Row left={`GST ${r.rate} on ${r.taxable}`} right="" />
              {r.cgst && <Row left="  CGST" right={r.cgst} />}
              {r.sgst && <Row left="  SGST" right={r.sgst} />}
              {r.igst && <Row left="  IGST" right={r.igst} />}
              {r.cess && <Row left="  Cess" right={r.cess} />}
            </div>
          ))}
        </>
      )}
      {model.upiUri && (
        <>
          <Rule />
          <p className="text-center font-semibold">Scan to pay by UPI</p>
          <Qr value={model.upiUri} size={narrow ? 110 : 130} />
        </>
      )}
      <Rule />
      <div className="text-center">
        {model.footerLines.map((l, i) => (
          <p key={i}>{l}</p>
        ))}
        {model.receiptUrl && model.kind === 'invoice' && (
          <div className="mt-2">
            <Qr value={model.receiptUrl} size={narrow ? 80 : 90} />
            <p className="text-[0.85em]">Scan for your e-receipt</p>
          </div>
        )}
        {model.poweredBy && <p className="mt-2 text-[0.85em]">Powered by ShriPOS</p>}
      </div>
    </div>
  );
}

/** Kitchen order ticket: large text, no prices. */
export function KotView({ kot, outletName, paperWidth = '80mm' }) {
  const narrow = paperWidth === '58mm';
  return (
    <div
      className={`mx-auto bg-white px-2 py-3 font-mono text-black ${narrow ? 'w-[58mm] text-[12px]' : 'w-[80mm] text-[14px]'}`}
    >
      <p className="text-center text-[1.4em] font-black">{kot.type === 'cancel' ? '*** CANCEL ***' : 'KOT'}</p>
      <p className="text-center">
        {outletName} · {kot.station.toUpperCase()}
      </p>
      <div className="my-1.5 border-t border-dashed border-black" />
      <div className="flex justify-between">
        <span>KOT #{kot.kotNo}</span>
        <span>{new Date(kot.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
      </div>
      {kot.tokenNo != null && <p className="text-[1.6em] font-black">TOKEN {kot.tokenNo}</p>}
      {kot.tableNo && <p className="text-[1.3em] font-bold">TABLE {kot.tableNo}</p>}
      <p className="uppercase">{kot.orderType.replace('_', '-')}</p>
      <div className="my-1.5 border-t border-dashed border-black" />
      {kot.lines.map((l, i) => (
        <div key={i} className="mb-1.5">
          <p className="text-[1.25em] font-bold">
            {kot.type === 'cancel' ? '-' : ''}
            {l.qty} × {l.name}
            {l.variantName ? ` (${l.variantName})` : ''}
          </p>
          {l.modifiers?.map((m) => (
            <p key={m} className="pl-3">
              + {m}
            </p>
          ))}
          {l.note && <p className="pl-3 font-bold">NOTE: {l.note}</p>}
        </div>
      ))}
      <div className="my-1.5 border-t border-dashed border-black" />
      <p className="text-center">{kot.createdBy ?? ''}</p>
    </div>
  );
}
